import type { MetricDashboardSource } from "@/metrics/catalog"
import { isBinaryMetric } from "@/metrics/metric-display"
import type { MetricDirectoryEntry } from "@/metrics/registry"

import type { ChartDataPoint, StatisticDataResult } from "../../types/chart"
import { AGGREGATION_TYPE } from "../../types/core"
import {
  getRowValue,
  type BuildChartDataParams,
  type DashboardChartRow,
} from "./chartQueryState"
import { formatChartDateLabel } from "./dateFormatting"
import { fillMissingBuckets } from "./fillMissingBuckets"
import { getSplitKeyFromItem } from "./splitKeys"

export * from "./chartQueryState"

export const buildChartData = ({
  bucketInterval,
  config,
  data,
  effectiveDateRange,
  isHistogramVisualization,
  isTopListVisualization,
  isWholeRangeVisualization,
}: BuildChartDataParams): ChartDataPoint[] => {
  if (!data || data.length === 0) {
    return []
  }

  if (isHistogramVisualization) {
    return (data as { value?: number }[]).map((item, index) => ({
      date: String(index),
      timestamp: index,
      bucketTime: String(index),
      value: item.value ?? undefined,
    }))
  }

  if (isTopListVisualization || isWholeRangeVisualization) {
    const isStringMetric =
      config.metricOutputType === "string" &&
      config.aggregation !== AGGREGATION_TYPE.SUCCESS
    const groupBy = config.groupBy || config.splitBy
    const isGrouped = Boolean(groupBy || config.groupByMetadataKey)
    const row: Record<string, number | string> = {
      date: "total",
      timestamp: 0,
      bucketTime: "",
    }

    for (const item of data as DashboardChartRow[]) {
      const value = getRowValue(item, config.aggregation)
      if (value === undefined) {
        continue
      }
      const key = isStringMetric
        ? isGrouped
          ? `${getSplitKeyFromItem(groupBy, item, config.groupByMetadataKey)}\u001E${item.string_value ?? "Unknown"}`
          : (item.string_value ?? "Unknown")
        : isGrouped
          ? getSplitKeyFromItem(groupBy, item, config.groupByMetadataKey)
          : "value"

      row[key] = ((row[key] as number) ?? 0) + value
    }

    return [row] as ChartDataPoint[]
  }

  const isStringMetric =
    config.metricOutputType === "string" &&
    config.aggregation !== AGGREGATION_TYPE.SUCCESS &&
    config.aggregation !== AGGREGATION_TYPE.AVG
  const groupedData: Record<string, Record<string, string | number>> = {}

  for (const item of data as DashboardChartRow[]) {
    if (!item.run_occurred_at) {
      continue
    }

    const bucketTime = new Date(item.run_occurred_at)
    if (Number.isNaN(bucketTime.getTime())) {
      continue
    }

    const timeKey = bucketTime.toISOString()

    if (!groupedData[timeKey]) {
      groupedData[timeKey] = {
        date: formatChartDateLabel(bucketTime, bucketInterval),
        timestamp: bucketTime.getTime(),
        bucketTime: timeKey,
      }
    }

    const groupBy = config.groupBy || config.splitBy
    const isGrouped = Boolean(groupBy || config.groupByMetadataKey)
    if (isStringMetric) {
      const stringValue = item.string_value ?? "Unknown"
      const value = getRowValue(item, config.aggregation)
      if (value === undefined) {
        continue
      }
      if (isGrouped) {
        const splitKey = getSplitKeyFromItem(
          groupBy,
          item,
          config.groupByMetadataKey,
        )
        groupedData[timeKey][`${splitKey}\u001E${stringValue}`] =
          ((groupedData[timeKey][
            `${splitKey}\u001E${stringValue}`
          ] as number) ?? 0) + value
      } else {
        groupedData[timeKey][stringValue] =
          ((groupedData[timeKey][stringValue] as number) ?? 0) + value
      }
      continue
    }

    const value = getRowValue(item, config.aggregation)
    if (value === undefined) {
      continue
    }
    if (isGrouped) {
      groupedData[timeKey][
        getSplitKeyFromItem(groupBy, item, config.groupByMetadataKey)
      ] = value
    } else {
      groupedData[timeKey].value = value
    }
  }

  const sortedChartData = Object.values(groupedData).sort(
    (left, right) =>
      new Date(left.bucketTime).getTime() -
      new Date(right.bucketTime).getTime(),
  ) as ChartDataPoint[]

  if (effectiveDateRange?.start && effectiveDateRange?.end) {
    return fillMissingBuckets(
      sortedChartData,
      effectiveDateRange.start,
      effectiveDateRange.end,
      bucketInterval,
    )
  }

  return sortedChartData
}

export const getChartQueryResult = ({
  flexibleError,
  flexibleLoading,
  flexibleQueryData,
  humanReviewData,
  humanReviewError,
  humanReviewLoading,
  isHumanReview,
  isStatisticVisualization,
  statisticData,
  statisticError,
  statisticLoading,
}: {
  flexibleError: object | null
  flexibleLoading: boolean
  flexibleQueryData: DashboardChartRow[] | undefined
  humanReviewData: DashboardChartRow[] | null
  humanReviewError: object | null
  humanReviewLoading: boolean
  isHumanReview: boolean
  isStatisticVisualization: boolean
  statisticData: StatisticDataResult[] | null
  statisticError: object | null
  statisticLoading: boolean
}) => ({
  data: isHumanReview ? humanReviewData : flexibleQueryData,
  error: isHumanReview
    ? humanReviewError
    : flexibleError || (isStatisticVisualization ? statisticError : null),
  isLoading: isHumanReview
    ? humanReviewLoading
    : flexibleLoading || (isStatisticVisualization && statisticLoading),
  statisticData: isStatisticVisualization ? statisticData : null,
})

// Prefer entry.semantic_type when the directory provides one; else fall back
// to the hardcoded manager list inside isBinaryMetric.
export const isMetricBinary = (
  metric?: MetricDashboardSource | null,
  entry?: MetricDirectoryEntry,
): boolean => {
  if (entry?.semantic_type === "binary") {
    return true
  }
  return metric ? isBinaryMetric(metric) : false
}
